import { ChangeEvent, FC, MouseEvent, RefObject } from 'react'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import TablePagination from '@mui/material/TablePagination'
import Paper from '@mui/material/Paper'
import IconButton from '@mui/material/IconButton'
import VpnKeyIcon from '@mui/icons-material/VpnKey'
import { ICustomer } from 'dto/users.dto'
import { Wrapper } from '../Styles.elements'
import CustomersTableHead, {
  CustomersHeadCell,
  CustomersSortField,
  CustomersSortOrder,
} from './CustomersTableHead'

const HEAD_CELLS: CustomersHeadCell[] = [
  { id: 'id', label: 'id' },
  { id: 'first_name', label: 'Имя' },
  { id: 'last_name', label: 'Фамилия' },
  { id: 'phone', label: 'Тел.' },
  { id: 'email', label: 'Email' },
  { id: 'city', label: 'Город' },
  { id: 'pizzeria_id', label: 'Пиццерия' },
  { id: 'orders_count', label: 'Заказов' },
  { id: 'orders_total_sum', label: 'Сумма' },
  { id: 'create_at', label: 'Регистрация' },
  { id: 'last_order_at', label: 'Последний заказ' },
]

interface CustomersTableProps {
  /** Строки клиентов */
  customers: ICustomer[]
  /** Общее число записей */
  total: number
  /** Текущая страница (0-based) */
  page: number
  /** Размер страницы */
  rowsPerPage: number
  /** Направление сортировки */
  order: CustomersSortOrder
  /** Поле сортировки */
  orderBy: CustomersSortField
  /** Ref таблицы для Excel */
  tableRef: RefObject<HTMLTableElement>
  /**
   * Смена сортировки.
   * @param event — клик
   * @param property — поле
   */
  onRequestSort: (
    event: MouseEvent<unknown>,
    property: CustomersSortField
  ) => void
  /**
   * Смена страницы.
   * @param event — событие
   * @param newPage — номер страницы
   */
  onPageChange: (event: unknown, newPage: number) => void
  /**
   * Смена размера страницы.
   * @param event — событие
   */
  onRowsPerPageChange: (event: ChangeEvent<HTMLInputElement>) => void
  /**
   * Открытие диалога пароля.
   * @param customer — клиент
   */
  onOpenPasswordDialog: (customer: ICustomer) => void
}

/**
 * Форматирует дату для ячейки таблицы.
 * @param value — ISO-строка или null
 */
const formatDateTime = (value?: string | null): string => {
  if (!value) {
    return '—'
  }
  const dateValue = new Date(value)
  if (Number.isNaN(dateValue.getTime())) {
    return '—'
  }
  return dateValue.toLocaleString('ru-RU')
}

/**
 * Форматирует сумму заказов.
 * @param value — число
 */
const formatMoney = (value?: number | null): string => {
  const amount = typeof value === 'number' ? value : 0
  return `${amount.toLocaleString('ru-RU')} ₽`
}

/**
 * Возвращает текст ячейки или тире.
 * @param value — значение
 */
const displayValue = (value?: string | number | null): string => {
  if (value === null || value === undefined || value === '') {
    return '—'
  }
  return String(value)
}

/**
 * Таблица клиентов в стиле orders.
 * @param props — данные и колбэки таблицы
 */
const CustomersTable: FC<CustomersTableProps> = ({
  customers,
  total,
  page,
  rowsPerPage,
  order,
  orderBy,
  tableRef,
  onRequestSort,
  onPageChange,
  onRowsPerPageChange,
  onOpenPasswordDialog,
}) => {
  return (
    <Wrapper>
      <TableContainer
        component={Paper}
        sx={{ overflowX: 'auto', maxWidth: '100%', boxShadow: 'none' }}
      >
        <Table
          size='small'
          aria-label='customers table'
          ref={tableRef}
          sx={{ minWidth: 960 }}
        >
          <CustomersTableHead
            order={order}
            orderBy={orderBy}
            headCells={HEAD_CELLS}
            onRequestSort={onRequestSort}
          />
          <TableBody>
            {customers?.map((customer, customerIndex) => (
              <TableRow
                key={customer?.id}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell component='th' scope='row'>
                  {page * rowsPerPage + customerIndex + 1}
                </TableCell>
                <TableCell align='right'>{customer?.id}</TableCell>
                <TableCell align='right'>
                  {displayValue(customer?.first_name)}
                </TableCell>
                <TableCell align='right'>
                  {displayValue(customer?.last_name)}
                </TableCell>
                <TableCell align='right'>
                  {displayValue(customer?.phone)}
                </TableCell>
                <TableCell align='right'>
                  {displayValue(customer?.email)}
                </TableCell>
                <TableCell align='right'>
                  {displayValue(customer?.city)}
                </TableCell>
                <TableCell align='right'>
                  {displayValue(customer?.pizzeria_label)}
                </TableCell>
                <TableCell align='right'>
                  {customer?.orders_count ?? 0}
                </TableCell>
                <TableCell align='right'>
                  {formatMoney(customer?.orders_total_sum)}
                </TableCell>
                <TableCell align='right'>
                  {formatDateTime(customer?.create_at)}
                </TableCell>
                <TableCell align='right'>
                  {formatDateTime(customer?.last_order_at)}
                </TableCell>
                <TableCell align='right'>
                  <IconButton
                    aria-label={`Управлять паролем клиента ${
                      customer?.first_name || customer?.id
                    }`}
                    onClick={() => onOpenPasswordDialog(customer)}
                  >
                    <VpnKeyIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 20, 30, 50]}
        component='div'
        count={total}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        labelRowsPerPage='Строк на странице:'
        labelDisplayedRows={({ from, to, count }) =>
          `${from}–${to} из ${count !== -1 ? count : `более чем ${to}`}`
        }
      />
    </Wrapper>
  )
}

export default CustomersTable
